import { EMPTY, of, throwError } from "rxjs";
import { Injectable, Injector } from "@angular/core";
import { catchError, finalize, map, switchMap } from "rxjs/operators";
import { Action, Selector, State, StateContext, Store, NgxsOnInit } from "@ngxs/store";
import { User } from "oidc-client-ts";
import { Router } from "@angular/router";

import {
	ConfigurationState,
	type ConfigurationStateModel,
} from "../../configuration/state/configuration.state";
import { OnSiteAuthService, PersonModelWithTokens } from "../AuthService";

import {
	AuthenticateServiceUser,
	AuthenticateUser,
	DeleteUserToken,
	RefreshServiceUserToken,
	SetServiceUser,
	TokenExpired,
} from "./authentication.actions";

export interface AuthenticationStateModel {
	serviceUser: User | null;
	user: PersonModelWithTokens | null;
	isRefreshingToken: boolean;
}

@State<AuthenticationStateModel>({
	name: "authentication",
	defaults: {
		serviceUser: null,
		user: null,
		isRefreshingToken: false,
	},
})
@Injectable()
export class AuthenticationState implements NgxsOnInit {
	@Selector()
	public static isRefreshingToken(state: AuthenticationStateModel): boolean {
		return state.isRefreshingToken;
	}

	@Selector()
	public static state(state: AuthenticationStateModel): AuthenticationStateModel {
		return state;
	}

	@Selector()
	public static serviceUser(state: AuthenticationStateModel): User | null {
		return state.serviceUser;
	}

	@Selector()
	public static user(state: AuthenticationStateModel): PersonModelWithTokens | null {
		return state.user;
	}

	private _authService?: OnSiteAuthService;

	private get authService() {
		if (!this._authService) {
			this._authService = this.injector.get(OnSiteAuthService);
		}
		return this._authService;
	}

	constructor(
		private store: Store,
		private router: Router,
		private injector: Injector,
	) {
		// No longer need to call initializeServiceUser here
	}

	/**
	 * NGXS lifecycle hook that runs after the state has been initialized and restored from storage
	 */
	ngxsOnInit(ctx: StateContext<AuthenticationStateModel>): void {
		this.initializeServiceUser(ctx);
	}

	/**
	 * Creates a proper User object from the deserialized state
	 * This is necessary because NGXS storage plugin deserializes to a plain object
	 * without prototype methods from the User class
	 */
	private initializeServiceUser(ctx: StateContext<AuthenticationStateModel>): void {
		const state = ctx.getState();

		// If we have a serviceUser with tokens but it's not a proper User instance
		if (state?.serviceUser?.access_token && !(state.serviceUser instanceof User)) {
			const storedUser = state.serviceUser as any;

			// Convert to TokenResult format for createUserFromTokenResult
			const tokenResult = {
				access_token: storedUser.access_token,
				refresh_token: storedUser.refresh_token,
				token_type: storedUser.token_type || "Bearer",
				expires_in: storedUser.expires_at
					? Math.max(0, Math.floor((storedUser.expires_at * 1000 - Date.now()) / 1000))
					: 0,
				scope: "openid profile rooms_api", // Default scopes
			};

			// Create a proper User instance
			try {
				const user = this.authService.createUserFromTokenResult(tokenResult);
				// Update the state directly with ctx.patchState instead of dispatching an action
				ctx.patchState({ serviceUser: user });
			} catch (error) {
				console.error("Failed to recreate User from stored tokens:", error);
			}
		}
	}

	@Action(AuthenticateServiceUser)
	public authenticateServiceUser(ctx: StateContext<AuthenticationStateModel>) {
		const authenticationState = ctx.getState();
		const configState = this.store.selectSnapshot<ConfigurationStateModel>(
			ConfigurationState.state,
		);

		// Skip if token is already valid
		if (authenticationState.serviceUser && !authenticationState.serviceUser.expired) {
			return;
		}

		if (
			(!authenticationState.serviceUser || authenticationState.serviceUser?.expired) &&
			!authenticationState.user?.access_token
		) {
			return ctx.dispatch(new TokenExpired());
		}

		// Skip if IDP URL is not configured
		if (!configState.rootConfiguration?.idpUrl) {
			return;
		}

		// Always use anonymous authentication for service user
		return this.authService.loginSystemPerson(authenticationState.user?.access_token).pipe(
			map((serviceUser) => {
				// Set service user directly in state
				ctx.dispatch(new SetServiceUser(serviceUser));
				return serviceUser;
			}),
			catchError((error) => {
				console.error("Service user authentication failed:", error);
				// Re-throw the error for interceptors or components to handle
				return throwError(() => error);
			}),
		);
	}

	@Action(RefreshServiceUserToken)
	public refreshServiceUserToken(ctx: StateContext<AuthenticationStateModel>) {
		const state = ctx.getState();

		if (!state.serviceUser) {
			return of(null);
		}

		// Check if we have a refresh token
		if (!state.serviceUser.refresh_token) {
			// Handle case where we have no refresh token
			console.warn("Cannot refresh service user token - no refresh token available");
			return ctx.dispatch(new TokenExpired());
		}

		ctx.patchState({ isRefreshingToken: true });

		// We use the observable for side effects, so we use a different pattern
		return this.authService.refreshSystemToken(state.serviceUser.refresh_token || "").pipe(
			switchMap((user) => {
				if (user) {
					return ctx.dispatch(new SetServiceUser(user));
				}
				return of(null);
			}),
			catchError((error) => {
				console.error("Failed to refresh service user token:", error);
				// Dispatch token expired action to trigger login flow
				this.router.navigate(["admin", "refresh-service-user-login"]);
				return EMPTY;
			}),
		);
	}

	@Action(TokenExpired)
	public handleTokenExpired(ctx: StateContext<AuthenticationStateModel>, action: TokenExpired) {
		const state = ctx.getState();
		if (state.serviceUser?.refresh_token) {
			return ctx.dispatch(new RefreshServiceUserToken());
		}

		if (state.serviceUser && action.redirectToLogin) {
			this.router.navigate(["admin", "refresh-service-user-login"]);
			return EMPTY;
		}

		return of(null);
	}

	@Action(AuthenticateUser)
	public authenticateUser(ctx: StateContext<AuthenticationStateModel>, action: AuthenticateUser) {
		const configState = this.store.selectSnapshot<ConfigurationStateModel>(
			ConfigurationState.state,
		);

		// Skip if IDP URL is not configured
		if (!configState.rootConfiguration?.idpUrl) {
			return;
		}

		// First ensure any previous user token is cleared
		ctx.patchState({ user: null, isRefreshingToken: true });

		return this.authService.loginApiKey(action.pin, action.resourceId, undefined).pipe(
			catchError((error) => {
				console.error("Error authenticating user:", error);
				ctx.patchState({ isRefreshingToken: false });
				return throwError(() => error);
			}),
			switchMap((user) => {
				// Set the user token - but we don't persist it to localStorage
				// This is temporary and will be discarded after use
				if (user && user.access_token) {
					ctx.patchState({ user: user });
				} else {
					ctx.patchState({ isRefreshingToken: false });
					return of(null);
				}

				return ctx.dispatch(new AuthenticateServiceUser()).pipe(
					catchError((error) => {
						console.error("Service user authentication after user login failed:", error);
						return of(null);
					}),
					finalize(() => {
						ctx.patchState({ isRefreshingToken: false });
					}),
				);
			}),
		);
	}

	@Action(SetServiceUser)
	public setServiceUser(ctx: StateContext<AuthenticationStateModel>, action: SetServiceUser) {
		ctx.patchState({
			serviceUser: action.serviceUser,
			isRefreshingToken: false,
		});

		// Return null to support chain operators
		return of(null);
	}

	@Action(DeleteUserToken)
	public deleteUserToken(ctx: StateContext<AuthenticationStateModel>) {
		ctx.patchState({
			user: null,
			isRefreshingToken: false,
		});
	}
}
